import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Home, Shield, DollarSign, List, ArrowRight, CheckCircle2, Calculator } from 'lucide-react';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
export function SimonsLandlord({
  onBack,
  setActivePage
}: {
  onBack: () => void;
  setActivePage: (p: any) => void;
}) {
  const [units, setUnits] = useState(5);
  return <div className="min-h-screen bg-[#F8FAFC] text-[#002366] font-sans" data-id="element-1733">
      <SimonsHeader activePage="landlord" setActivePage={setActivePage} data-id="element-1734" />

      {/* Hero / Calculator */}
      <section className="py-24 px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-1735">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1736">
          <h1 className="text-5xl lg:text-6xl font-black mb-8 leading-tight tracking-tight" data-id="element-1737">
            Portfolio <br data-id="element-1738" />
            <span className="text-[#D32F2F]" data-id="element-1739">Management.</span>
          </h1>
          <p className="text-xl text-slate-500 mb-10 font-medium leading-relaxed" data-id="element-1740">
            Simplified insurance for real estate investors. Protect your cash
            flow and assets with a single, unified portfolio policy.
          </p>

          <div className="bg-white p-8 rounded-[24px] shadow-xl border border-slate-100" data-id="element-1741">
            <div className="flex items-center gap-3 mb-6" data-id="element-1742">
              <Calculator className="text-[#FFD700]" data-id="element-1743" />
              <h3 className="font-black uppercase tracking-widest text-sm" data-id="element-1744">
                Multi-Property Savings
              </h3>
            </div>
            <div className="space-y-6" data-id="element-1745">
              <div data-id="element-1746">
                <div className="flex justify-between mb-2" data-id="element-1747">
                  <span className="text-xs font-bold text-slate-400 uppercase" data-id="element-1748">
                    Number of Units
                  </span>
                  <span className="text-lg font-black" data-id="element-1749">{units}</span>
                </div>
                <input type="range" min="1" max="50" value={units} onChange={e => setUnits(parseInt(e.target.value))} className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#D32F2F]" data-id="element-1750" />
              </div>
              <div className="pt-4 border-t border-slate-50 flex justify-between items-center" data-id="element-1751">
                <span className="text-sm font-bold text-slate-500" data-id="element-1752">
                  Estimated Annual Savings
                </span>
                <span className="text-3xl font-black text-[#002366]" data-id="element-1753">
                  ${(units * 145).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="bg-[#002366] rounded-[32px] p-12 text-white shadow-2xl relative overflow-hidden" data-id="element-1754">
          <h2 className="text-2xl font-black mb-8 flex items-center gap-3" data-id="element-1755">
            <List className="text-[#FFD700]" data-id="element-1756" />
            Simplified Coverage
          </h2>
          <div className="space-y-4" data-id="element-1757">
            {[{
            title: 'Renters Liability',
            desc: 'Protection against tenant-related incidents.'
          }, {
            title: 'Structure Protection',
            desc: 'Full replacement cost for your physical assets.'
          }, {
            title: 'Loss of Income',
            desc: 'Cash flow security during property repairs.'
          }, {
            title: 'Legal Defense',
            desc: 'Expert representation for landlord-tenant disputes.'
          }].map((item, i) => <div key={i} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10" data-id="element-1758">
                <CheckCircle2 className="text-[#FFD700] shrink-0 mt-1" size={18} data-id="element-1759" />
                <div data-id="element-1760">
                  <p className="font-bold text-sm" data-id="element-1761">{item.title}</p>
                  <p className="text-xs text-white/40" data-id="element-1762">{item.desc}</p>
                </div>
              </div>)}
          </div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#D32F2F]/20 blur-3xl rounded-full" data-id="element-1763"></div>
        </div>
      </section>

      {/* Portfolio Review Form */}
      <section className="py-24 px-8 bg-white border-y border-slate-200" data-id="element-1764">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1765">
          <h2 className="text-3xl font-black mb-6" data-id="element-1766">Request Portfolio Review</h2>
          <p className="text-slate-500 font-medium mb-12" data-id="element-1767">
            Send us your current schedule of real estate and we'll provide a
            strategic consolidation quote.
          </p>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left" data-id="element-1768">
            <div className="space-y-2" data-id="element-1769">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1770">
                Full Name
              </label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" placeholder="Investor Name" data-id="element-1771" />
            </div>
            <div className="space-y-2" data-id="element-1772">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1773">
                Portfolio Size
              </label>
              <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" data-id="element-1774">
                <option data-id="element-1775">1-5 Properties</option>
                <option data-id="element-1776">5-20 Properties</option>
                <option data-id="element-1777">20+ Properties</option>
              </select>
            </div>
            <button className="md:col-span-2 mt-4 py-4 bg-[#002366] text-white rounded-xl font-black uppercase tracking-widest hover:bg-[#001a4d] transition-all shadow-xl shadow-[#002366]/20" data-id="element-1778">
              Start Review
            </button>
          </form>
        </div>
      </section>

      <SimonsFooter data-id="element-1779" />
    </div>;
}